import { useState, useEffect } from "react";
import { isAuthenticated } from "../utils/authHelper";
import api from "../api";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../constants";
import GoogleSignIn from "../components/GoogleLogin";
import { useTranslation } from "../LanguageContext";

function RegisterForm() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [password_conf, setPassword_conf] = useState("");
    const [email, setEmail] = useState("");
    const [validate_code, setvalidate_code] = useState("");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [validate_input, setvalidate_input] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [countdown, setCountdown] = useState(60);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleServerError = (serverErrors) => {
        const formattedErrors = [];
        if (serverErrors.username) {
            formattedErrors.push({ field: 'username', message: serverErrors.username[0] });
        }
        if (serverErrors.password) {
            serverErrors.password.forEach(error => {
                formattedErrors.push({ field: 'password', message: error });
            });
        }
        if (serverErrors.email) {
            formattedErrors.push({ field: 'email', message: serverErrors.email[0] });
        }
        if (serverErrors.code) {
            formattedErrors.push({ field: 'code', message: serverErrors.code[0] });
        }
        setErrors(formattedErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post("/api/user/register/", { username, password, email, validate_code, password_conf });

            if (res.status === 201) {
                setErrors([]);
                try {
                    const res = await api.post("/api/token", { username, password })
                    localStorage.setItem(ACCESS_TOKEN, res.data.access);
                    localStorage.setItem(REFRESH_TOKEN, res.data.refresh);
                    navigate("/profile")
                } catch (error) {
                    alert(t('register.error'))
                } finally {
                    setLoading(false)
                }
            }

            if (res.status === 200) {
                // Awaited on purpose: this used to be fire-and-forget, so a
                // code that never left the server still flipped the form to
                // "enter your code" and the user waited on an empty inbox.
                // A failure now throws into the catch below and is shown.
                await api.post("api/CreateVCode/", { email })
                setErrors([{ field: '', message: t('register.codeSent') }]);
                setvalidate_input(true)
                setIsActive(false)
            }

            if (res.status === 208) {
                setErrors([]);
                setvalidate_input(true)
            }
        } catch (error) {
            handleServerError(error.response.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let interval = null;
        if (!isActive && countdown > 0) {
            interval = setInterval(() => {
                setCountdown((countdown) => countdown - 1);
            }, 1000);
        } else if (countdown === 0) {
            clearInterval(interval);
            setIsActive(true);
            setCountdown(60);
        }
        return () => clearInterval(interval);
    }, [isActive, countdown]);

    const handleClick = async () => {
        // Resend. Same reason as above: only claim the code was sent once the
        // server says it was, and keep the countdown unlocked if it was not so
        // the user can try again straight away.
        try {
            await api.post("api/CreateVCode/", { email });
            setErrors([{ message: t('register.codeSent') }]);
            setIsActive(false);
        } catch (error) {
            handleServerError(error?.response?.data ?? {});
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-primary-50 flex items-center justify-center px-4 ">
            <div className="w-full max-w-md">
                {/* Back button */}
                <Link to="/" className="inline-flex items-center text-slate-500 hover:text-primary-600 transition mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span className="text-sm font-medium">{t('register.back')}</span>
                </Link>

                {/* Card */}
                <div className="card">
                    <h1 style={{color:"#00000094"}} className="text-2xl font-bold text-slate-800 mb-6">{t('register.title')}</h1>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            {/* <label className="label">{t('register.usernameLabel')}</label> */}
                            <input className="input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('register.usernamePlaceholder')} maxLength={30} required />
                        </div>
                        <div>
                            {/* <label className="label">{t('register.emailLabel')}</label> */}
                            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('register.emailPlaceholder')} maxLength={254} required />
                        </div>
                        <div>
                            {/* <label className="label">{t('register.passwordLabel')}</label> */}
                            <input className="input" type="password" pattern="\S+" title={t('register.noSpaces')} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('register.passwordPlaceholder')} maxLength={128} minLength={8} required />
                        </div>
                        <div>
                            {/* <label className="label">{t('register.confirmLabel')}</label> */}
                            <input className="input" type="password" pattern="\S+" title={t('register.noSpaces')} value={password_conf} onChange={(e) => setPassword_conf(e.target.value)} placeholder={t('register.confirmPlaceholder')} maxLength={128} minLength={8} required />
                        </div>

                        {/* Verification code */}
                        {validate_input && (
                            <div className="space-y-3 p-4 bg-primary-50 rounded-xl border border-primary-100">
                                <div className="flex gap-2">
                                    <input
                                        placeholder={t('register.codePlaceholder')}
                                        className="input flex-1"
                                        value={validate_code}
                                        onChange={(e) => setvalidate_code(e.target.value)}
                                        pattern="\d{6}"
                                        maxLength={6}
                                    />
                                    <button
                                        type="button"
                                        className="btn-secondary text-sm whitespace-nowrap"
                                        onClick={handleClick}
                                        disabled={!isActive}
                                    >
                                        {isActive ? t('register.resend') : `${countdown}s`}
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400">{t('register.spamNote')}</p>
                            </div>
                        )}

                        {errors.length > 0 && (
                            <ul className="list-none p-0 space-y-1">
                                {errors.map((error, index) => (
                                    <li key={index} className="text-sm text-red-600">{` ${error.message}`}</li>
                                ))}
                            </ul>
                        )}

                        <button
                            className="btn-primary w-full flex items-center justify-center"
                            type="submit"
                            disabled={loading}
                            style={{borderRadius:"50px"}}
                        >
                            {loading ? <div className="spinner-sm"></div> : t('register.submit')}
                        </button>
                    </form>

                    <p className="text-xs text-slate-400 mt-4 text-center">
                        {t('register.terms')}{' '}
                        <a href="/termsofuse" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{t('register.termsLink')}</a> {t('register.and')}{' '}
                        <a href="/privacypolicy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{t('register.privacyLink')}</a>{t('register.termsEnd')}
                    </p>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                        <div className="relative flex justify-center text-sm"><span className="bg-white px-3 text-slate-400">{t('register.or')}</span></div>
                    </div>

                    <p className="text-sm text-slate-500 text-center mb-3">{t('register.google')}</p>
                    <GoogleSignIn method="register" />
                </div>
            </div>
        </div>
    );
}

function Register() {
    if (isAuthenticated()) {
        return <Navigate to="/profile" />;
    }
    return <RegisterForm />
}
export default Register;